import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppBar, Box } from '@mui/material';
import Tabs, { tabsClasses } from '@mui/material/Tabs';
import { SubCategories } from '../subCategories/subCategories';
import { ServiceCategory } from 'orderme-api-integration-client';
import { ServiceClient } from '../../helpers/client';
import { RootState } from '../../redux/store';
import { ScrollIconButton, ServiceTab } from './serviceTabStyles';
import { LastTab, setTabValue } from '../../redux/reducers/tabsReducer';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useSwipeable } from 'react-swipeable';
import { GoodsList } from '../goods/goodsList';
import { isGoodsCategory } from '../../helpers/servicesHelper';
import { CategoryItem } from './categoryItem';

const StartScrollButton = () => {
  return (
    <ScrollIconButton disableRipple sx={{ marginRight: '4px' }}>
      <ArrowBackIosIcon />
    </ScrollIconButton>
  );
};
const EndScrollButton = () => {
  return (
    <ScrollIconButton disableRipple sx={{ marginLeft: '2px' }}>
      <ArrowForwardIosIcon />
    </ScrollIconButton>
  );
};

export function ServiceTabs() {
  const dispatch = useAppDispatch();
  const servicesClient: ServiceClient = useMemo(() => new ServiceClient(), []);
  const lastTab: LastTab = useAppSelector((state: RootState) => state.tabState);
  const customerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const [value, setValue] = useState(lastTab.tabValue);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>();
  const [goodsCategory, setGoodsCategory] = useState<ServiceCategory>();

  const serviceCategoriesFetcher = useCallback(() => {
    servicesClient
      .getCategories()
      .then((response) => {
        return setServiceCategories(response);
      })
      .catch((err) => console.log(err));
  }, [servicesClient]);

  useEffect(() => {
    serviceCategoriesFetcher();
  }, [customerInfo, serviceCategoriesFetcher]);

  useEffect(() => {
    setGoodsCategory(serviceCategories?.find(isGoodsCategory));
  }, [serviceCategories]);

  const handleTabChange = (newValue: number) => {
    if (newValue > -1 && newValue < serviceCategories?.length) {
      dispatch(setTabValue(newValue));
      setValue(newValue);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => handleTabChange(value + 1),
    onSwipedRight: () => handleTabChange(value - 1),
  });

  return (
    <Box sx={{ width: '100%', backgroundColor: 'background.paper' }}>
      <AppBar
        sx={{ backgroundColor: 'appBar.top.background', pb: '5px' }}
        elevation={0}
        position="static"
        color="default"
      >
        <Tabs
          sx={{
            overflow: 'auto',
            pl: '9px',
            pr: '7px',
            [`& .${tabsClasses.scrollButtons}`]: {
              '&.Mui-disabled': { opacity: 0.3 },
            },
          }}
          scrollButtons
          allowScrollButtonsMobile
          value={value}
          onChange={(_, index) => handleTabChange(index)}
          variant="scrollable"
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: 'secondary.main',
                color: 'secondary.contrastText',
                height: '3px',
              },
            },
          }}
          slots={{
            StartScrollButtonIcon: StartScrollButton,
            EndScrollButtonIcon: EndScrollButton,
          }}
        >
          {serviceCategories?.map((category, index) => {
            return <ServiceTab key={index} label={<CategoryItem category={category} />} />;
          })}
        </Tabs>
      </AppBar>
      {serviceCategories?.length > 0 && (
        <Box key={value} {...handlers}>
          {serviceCategories[value] && goodsCategory !== serviceCategories[value] ? (
            <SubCategories category={serviceCategories[value]} tabIndex={value} />
          ) : (
            <GoodsList />
          )}
        </Box>
      )}
    </Box>
  );
}
